import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Autocomplete,
  Grid,
  CircularProgress,
  InputAdornment,
  Typography,
  Stack,
  Collapse,
  IconButton,
  Icon,
  LinearProgress,
  Paper,
  useTheme,
  alpha,
  Card,
} from "@mui/material";
import { inventoryService } from "../../../../services/inventory.service";
import { IESDoc } from "../../../../../../app.interface";
import { convertUnits } from "../../../../utils/units";
import dayjs from "dayjs";
import { toast } from "react-toastify";

const THEME_COLORS = {
  primary: '#0891B2',
  primaryHover: '#0E7490',
  secondary: '#64748B',
  danger: '#DC2626',
  dangerHover: '#B91C1C',
  success: '#059669',
  background: {
    main: '#FFFFFF',
    light: '#F8FAFC',
    card: '#FFFFFF',
  },
  border: {
    light: alpha('#0891B2', 0.1),
    main: alpha('#0891B2', 0.2),
  },
  text: {
    primary: '#1E293B',
    secondary: '#64748B',
    light: '#94A3B8',
  }
};

export interface IngredientOption {
  _id: string;
  item_id: string;
  brand_name: string;
  unit: string;
  base_unit?: string;
  cost_per_unit?: number;
}

export interface ExtendedBatch {
  batch_id: string;
  product_id: string;
  batch_number: string;
  quantity: number;
  remaining_quantity: number;
  unit: string;
  cost_per_unit: number;
  total_cost: number;
  source_name: string;
  brand_name: string;
  storage_place_name: string;
  expiry_date: number;
}

export interface IngredientEntry {
  item: IngredientOption | null;
  quantity: number;
  unit: string;
  notes: string;
  selected_batches: Array<{
    batch_id: string;
    quantity: number;
    unit: string;
    cost_per_unit: number;
  }>;
  required_quantity: number;
  remaining_quantity: number;
  is_modified: boolean;
  // UI state properties
  loadingBatches: boolean;
  batches: ExtendedBatch[];
  expanded: boolean;
}

interface IngredientDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (ingredients: IngredientEntry[]) => void;
  mode: "add" | "edit";
  currentItems?: Array<{
    ingredient_id: string;
    quantity: number;
    unit: string;
  }>;
}

export const IngredientDialog: React.FC<IngredientDialogProps> = ({
  open,
  onClose,
  onSave,
  mode,
  currentItems,
}) => {
  const theme = useTheme();
  const [ingredients, setIngredients] = useState<IngredientOption[]>([]);
  const [selectedIngredients, setSelectedIngredients] = useState<IngredientEntry[]>(
    currentItems?.map((item) => ({
      item: null,
      quantity: item.quantity,
      unit: item.unit,
      notes: "",
      selected_batches: [],
      required_quantity: item.quantity,
      remaining_quantity: item.quantity,
      is_modified: false,
      loadingBatches: false,
      batches: [],
      expanded: false,
    })) || [
      {
        item: null,
        quantity: 1,
        unit: "",
        notes: "",
        selected_batches: [],
        required_quantity: 1,
        remaining_quantity: 1,
        is_modified: false,
        loadingBatches: false,
        batches: [],
        expanded: false,
      },
    ]
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadIngredients = async () => {
      try {
        setLoading(true);
        const response = await inventoryService.listIngredients({
          pageIndex: 0,
          pageSize: 100,
          sortField: "created_at",
          sortOrder: "desc"
        });

        const mappedIngredients = response.data.map((item: any) => ({
          _id: item._id,
          name: item.name,
          brand_name: item.name,
          unit: item.unit,
          base_unit: item.base_unit || item.unit,
          item_id: item.ingredient_id,
        }));

        console.log('Mapped ingredients:', mappedIngredients);
        setIngredients(mappedIngredients);
      } catch (error: any) {
        console.error("Error loading ingredients:", error);
        if (error?.response?.data) {
          console.error("Response data:", error.response.data);
        }
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      loadIngredients();
    }
  }, [open]);

  const loadBatches = async (ingredientId: string, index: number) => {
    try {
      setSelectedIngredients(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], loadingBatches: true };
        return updated;
      });

      const response = await inventoryService.listBatches({
        ingredient_id: ingredientId,
        pageIndex: 0,
        pageSize: 100,
        sortField: "expiry_date",
        sortOrder: "asc",
        include_wasted: false,
      });

      const batches = response.data.batches.map((batch: any) => ({
        ...batch,
        storage_place_name: batch.storage_place_name || 'Default Storage',
      }));

      setSelectedIngredients(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          batches,
          loadingBatches: false,
        };
        return updated;
      });

    } catch (error) {
      console.error("Error loading batches:", error);
      setSelectedIngredients(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], loadingBatches: false };
        return updated;
      });
    }
  };

  const handleIngredientChange = async (index: number, newValue: IngredientOption | null) => {
    setSelectedIngredients((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        item: newValue,
        unit: newValue?.unit || "",
        selected_batches: [],
        batches: [],
      };
      return updated;
    });

    if (newValue?.item_id) {
      await loadBatches(newValue.item_id, index);
    }
  };

  const handleQuantityChange = (index: number, value: number) => {
    setSelectedIngredients((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        quantity: value,
      };
      return updated;
    });
  };

  const handleBatchSelect = (ingredientIndex: number, batch: ExtendedBatch, quantity: number) => {
    setSelectedIngredients((prev) => {
      const updated = [...prev];
      const ingredient = updated[ingredientIndex];
      
      // Find if batch is already selected
      const existingBatchIndex = ingredient.selected_batches.findIndex(
        (b) => b.batch_id === batch.batch_id
      );

      let newSelectedBatches = [...ingredient.selected_batches];

      if (existingBatchIndex >= 0) {
        if (quantity <= 0) {
          // Remove batch if quantity is 0 or negative
          newSelectedBatches = newSelectedBatches.filter((_, i) => i !== existingBatchIndex);
        } else {
          // Update existing batch quantity
          newSelectedBatches[existingBatchIndex] = {
            batch_id: batch.batch_id,
            quantity: quantity,
            unit: batch.unit,
            cost_per_unit: batch.cost_per_unit,
          };
        }
      } else if (quantity > 0) {
        // Add new batch
        newSelectedBatches.push({
          batch_id: batch.batch_id,
          quantity: quantity,
          unit: batch.unit,
          cost_per_unit: batch.cost_per_unit,
        });
      }

      // Calculate remaining quantity
      const totalAllocated = newSelectedBatches.reduce((total, selectedBatch) => {
        const convertedQuantity = selectedBatch.unit !== ingredient.unit
          ? convertUnits(selectedBatch.quantity, selectedBatch.unit, ingredient.unit) || selectedBatch.quantity
          : selectedBatch.quantity;
        return total + convertedQuantity;
      }, 0);

      updated[ingredientIndex] = {
        ...ingredient,
        selected_batches: newSelectedBatches,
        remaining_quantity: ingredient.required_quantity - totalAllocated,
      };

      return updated;
    });
  };

  const handleNotesChange = (index: number, value: string) => {
    setSelectedIngredients((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        notes: value,
      };
      return updated;
    });
  };

  const handleAddMore = () => {
    setSelectedIngredients((prev) => [
      ...prev,
      {
        item: null,
        quantity: 1,
        unit: "",
        notes: "",
        selected_batches: [],
        required_quantity: 1,
        remaining_quantity: 1,
        is_modified: false,
        loadingBatches: false,
        batches: [],
        expanded: false,
      },
    ]);
  };

  const handleRemove = (index: number) => {
    setSelectedIngredients((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      // Validate all ingredients have been selected
      const hasEmptyIngredients = selectedIngredients.some(
        (item) => !item.item || item.quantity <= 0
      );
      if (hasEmptyIngredients) {
        toast.error("Please select all ingredients and enter valid quantities");
        return;
      }

      // Map the selected ingredients to include all necessary details
      const ingredientsToSave: IngredientEntry[] = selectedIngredients.map((item) => {
        // Get the complete batch details from the batches array
        const selectedBatchesWithDetails = item.selected_batches.map(selectedBatch => {
          const batchDetails = item.batches.find(b => b.batch_id === selectedBatch.batch_id);
          return {
            batch_id: selectedBatch.batch_id,
            quantity: selectedBatch.quantity,
            unit: selectedBatch.unit,
            cost_per_unit: batchDetails?.cost_per_unit || 0,
          };
        });

        return {
          item: item.item,
          quantity: item.quantity,
          unit: item.unit,
          notes: item.notes,
          selected_batches: selectedBatchesWithDetails,
          required_quantity: item.quantity,
          remaining_quantity: item.quantity - (item.selected_batches?.reduce((total, batch) => {
            const convertedQuantity = batch.unit !== item.unit
              ? convertUnits(batch.quantity, batch.unit, item.unit) || batch.quantity
              : batch.quantity;
            return total + convertedQuantity;
          }, 0) || 0),
          is_modified: false,
          loadingBatches: false,
          batches: [],
          expanded: false,
        };
      });

      // Call the onSave callback with the complete ingredient data
      onSave(ingredientsToSave);
    } catch (error) {
      console.error("Error saving ingredients:", error);
      toast.error("Failed to save ingredients");
    }
  };

  const toggleExpanded = (index: number) => {
    setSelectedIngredients((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        expanded: !updated[index].expanded,
      };
      return updated;
    });
  };

  const calculateRemainingQuantity = (entry: IngredientEntry) => {
    if (!entry.item) return 0;
    
    const totalSelected = entry.selected_batches.reduce((sum, batch) => {
      const convertedQty = batch.unit !== entry.unit
        ? convertUnits(batch.quantity, batch.unit, entry.unit) || batch.quantity
        : batch.quantity;
      return sum + convertedQty;
    }, 0);

    return entry.quantity - totalSelected;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${THEME_COLORS.border.light}`,
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: THEME_COLORS.background.main,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Montserrat',
              fontWeight: 600,
              color: THEME_COLORS.text.primary,
              mb: 0.5,
            }}
          >
            {mode === 'edit' ? 'Edit Ingredient' : 'Add Ingredients'}
          </Typography>
          <Typography variant="body2" sx={{ color: THEME_COLORS.text.secondary }}>
            {mode === 'edit'
              ? 'Modify the ingredient details and batch selection'
              : 'Search and add ingredients to your recipe with their quantities and batches'}
          </Typography>
        </Box>
        {mode === 'add' && selectedIngredients.length < ingredients.length && (
          <Button
            startIcon={<Icon>add</Icon>}
            onClick={handleAddMore}
            variant="contained"
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': { bgcolor: THEME_COLORS.primaryHover },
            }}
          >
            Add Another
          </Button>
        )}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ color: THEME_COLORS.primary }} />
          </Box>
        ) : ingredients.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Icon sx={{ fontSize: 48, color: alpha(THEME_COLORS.primary, 0.2), mb: 2 }}>restaurant</Icon>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: THEME_COLORS.text.primary, fontFamily: 'Montserrat', fontWeight: 600 }}
            >
              No Ingredients Available
            </Typography>
            <Typography variant="body2" sx={{ color: THEME_COLORS.text.secondary }}>
              All ingredients have been added or no ingredients exist.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {selectedIngredients.map((entry, index) => (
              <Card
                key={index}
                sx={{
                  bgcolor: THEME_COLORS.background.light,
                  border: `1px solid ${THEME_COLORS.border.light}`,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: THEME_COLORS.primary,
                    boxShadow: `0 0 0 1px ${THEME_COLORS.primary}`,
                  },
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Autocomplete
                        options={ingredients}
                        loading={loading}
                        value={entry.item}
                        onChange={(_, newValue) => handleIngredientChange(index, newValue)}
                        getOptionLabel={(option) => option.brand_name}
                        sx={{ minWidth: 320, flex: 1 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Ingredient"
                            required
                            size="small"
                            sx={{
                              minWidth: 320,
                              '& .MuiOutlinedInput-root': {
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME_COLORS.primary,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME_COLORS.primary,
                                },
                              },
                            }}
                          />
                        )}
                      />
                      {mode === 'add' && selectedIngredients.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(index)}
                          sx={{
                            color: THEME_COLORS.danger,
                            '&:hover': { bgcolor: alpha(THEME_COLORS.danger, 0.08) },
                          }}
                        >
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>
                      )}
                    </Box>
                    {entry.item && (
                      <>
                        <Stack direction="row" spacing={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Required Quantity"
                            type="number"
                            value={entry.quantity}
                            onChange={(e) => handleQuantityChange(index, parseFloat(e.target.value) || 0)}
                            InputProps={{
                              endAdornment: entry.unit && (
                                <InputAdornment position="end">{entry.unit}</InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME_COLORS.primary,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME_COLORS.primary,
                                },
                              },
                            }}
                          />
                        </Stack>
                        <TextField
                          fullWidth
                          size="small"
                          label="Notes"
                          value={entry.notes}
                          onChange={(e) => handleNotesChange(index, e.target.value)}
                          multiline
                          rows={2}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: THEME_COLORS.primary,
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: THEME_COLORS.primary,
                              },
                            },
                          }}
                        />
                        {/* Batch selection UI */}
                        {entry.item && (
                          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: THEME_COLORS.background.card }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle2" sx={{ color: THEME_COLORS.text.primary, fontWeight: 600 }}>
                                Available Batches
                              </Typography>
                              <IconButton size="small" onClick={() => toggleExpanded(index)} sx={{ color: THEME_COLORS.primary }}>
                                <Icon>{entry.expanded ? 'expand_less' : 'expand_more'}</Icon>
                              </IconButton>
                            </Stack>
                            <Collapse in={entry.expanded}>
                              {entry.loadingBatches ? (
                                <Box sx={{ py: 2 }}>
                                  <LinearProgress sx={{
                                    bgcolor: alpha(THEME_COLORS.primary, 0.08),
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: THEME_COLORS.primary,
                                    },
                                  }} />
                                </Box>
                              ) : entry.batches.length === 0 ? (
                                <Typography variant="body2" color={THEME_COLORS.text.secondary} sx={{ py: 2 }}>
                                  No batches available
                                </Typography>
                              ) : (
                                <Stack spacing={1} sx={{ mt: 2 }}>
                                  {entry.batches.map((batch) => {
                                    const isSelected = entry.selected_batches.some(
                                      (b) => b.batch_id === batch.batch_id
                                    );
                                    const selectedBatch = entry.selected_batches.find(
                                      (b) => b.batch_id === batch.batch_id
                                    );
                                    const availableQuantity = batch.remaining_quantity;
                                    const requiredQuantity = entry.quantity;
                                    const availabilityPercentage = (availableQuantity / requiredQuantity) * 100;
                                    return (
                                      <Box
                                        key={batch.batch_id}
                                        sx={{
                                          p: 1.5,
                                          border: `1px solid ${isSelected ? THEME_COLORS.primary : THEME_COLORS.border.light}`,
                                          borderRadius: 1,
                                          bgcolor: isSelected ? alpha(THEME_COLORS.primary, 0.04) : 'transparent',
                                          transition: 'border-color 0.2s',
                                        }}
                                      >
                                        <Grid container spacing={2}>
                                          <Grid item xs={12}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                              <Box>
                                                <Typography variant="subtitle2" sx={{ color: THEME_COLORS.text.primary, fontWeight: 500 }}>
                                                  Batch #{batch.batch_number}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: THEME_COLORS.text.secondary }}>
                                                  {batch.source_name} • {batch.storage_place_name}
                                                </Typography>
                                              </Box>
                                              <Typography variant="caption" sx={{ color: THEME_COLORS.text.secondary }}>
                                                Expires: {dayjs(batch.expiry_date).format('MMM D, YYYY')}
                                              </Typography>
                                            </Stack>
                                          </Grid>
                                          <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Typography variant="caption" sx={{ color: THEME_COLORS.text.secondary, minWidth: 100 }}>
                                                Available: {availableQuantity} {batch.unit}
                                              </Typography>
                                              <LinearProgress
                                                variant="determinate"
                                                value={Math.min(availabilityPercentage, 100)}
                                                sx={{
                                                  flex: 1,
                                                  height: 6,
                                                  borderRadius: 1,
                                                  bgcolor: alpha(THEME_COLORS.primary, 0.08),
                                                  '& .MuiLinearProgress-bar': {
                                                    backgroundColor: THEME_COLORS.primary,
                                                  },
                                                }}
                                              />
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  minWidth: 40,
                                                  textAlign: 'right',
                                                  color: THEME_COLORS.primary,
                                                }}
                                              >
                                                {Math.round(availabilityPercentage)}%
                                              </Typography>
                                            </Box>
                                          </Grid>
                                          <Grid item xs={12}>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                              <TextField
                                                size="small"
                                                type="number"
                                                label="Quantity"
                                                value={selectedBatch?.quantity || ''}
                                                onChange={(e) =>
                                                  handleBatchSelect(
                                                    index,
                                                    batch,
                                                    parseFloat(e.target.value) || 0
                                                  )
                                                }
                                                InputProps={{
                                                  endAdornment: (
                                                    <InputAdornment position="end">{batch.unit}</InputAdornment>
                                                  ),
                                                }}
                                                sx={{ width: 200 }}
                                              />
                                              <Typography variant="caption" sx={{ color: THEME_COLORS.text.secondary }}>
                                                Cost: {batch.cost_per_unit?.toFixed(3)} BHD/{batch.unit}
                                              </Typography>
                                              {selectedBatch && (
                                                <Typography variant="caption" sx={{ color: THEME_COLORS.primary }}>
                                                  Total: {(selectedBatch.quantity * batch.cost_per_unit)?.toFixed(3)} BHD
                                                </Typography>
                                              )}
                                            </Stack>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              )}
                            </Collapse>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" sx={{ color: THEME_COLORS.text.secondary }}>
                                Remaining Quantity: {calculateRemainingQuantity(entry)} {entry.unit}
                              </Typography>
                            </Box>
                          </Paper>
                        )}
                      </>
                    )}
                  </Stack>
                </Box>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: `1px solid ${THEME_COLORS.border.light}`,
          px: 3,
          py: 2,
          bgcolor: THEME_COLORS.background.main,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: THEME_COLORS.text.primary,
            '&:hover': { bgcolor: alpha(THEME_COLORS.primary, 0.04) },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={<Icon>save</Icon>}
          sx={{
            bgcolor: THEME_COLORS.primary,
            '&:hover': { bgcolor: THEME_COLORS.primaryHover },
            '&.Mui-disabled': {
              bgcolor: alpha(THEME_COLORS.primary, 0.12),
              color: alpha(THEME_COLORS.primary, 0.26),
            },
          }}
        >
          {mode === 'edit' ? 'Save Changes' : 'Add Selected Items'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 